import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";

/**
 * <WhatsNewBanner/> — a slim, dismissible "here's what changed" strip that
 * shows once per user per version. Placed at the very top of high-traffic
 * pages (/, /analyze, /report) so returning visitors notice the update
 * without a splash modal.
 *
 * To ship a new update: bump `CURRENT_VERSION` and update the copy inline.
 * Users who dismissed the previous version see the new one automatically.
 *
 * Dismiss state is stored in localStorage keyed by version so it survives
 * across sessions but doesn't linger forever if we ship a new update.
 */
const CURRENT_VERSION = "20260810-enterprise-value";
const STORAGE_KEY = "cfograde_whatsnew_dismissed";

export default function WhatsNewBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      // Show if never seen or if we've bumped the version since last dismiss
      if (dismissed !== CURRENT_VERSION) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  function dismiss() {
    try {
      localStorage.setItem(STORAGE_KEY, CURRENT_VERSION);
    } catch {
      // ignore
    }
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      className="bg-[#1F382E] text-[#F7F5F0] border-b border-[#1F382E]/20"
      data-testid="whats-new-banner"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <Sparkles
            size={13}
            strokeWidth={1.75}
            className="text-[#C26D5C] shrink-0"
          />
          <p className="text-[13px] leading-snug truncate">
            <span className="text-[#C26D5C] font-medium tracking-wide uppercase text-[10px] mr-2">
              New
            </span>
            <span className="hidden sm:inline">
              Every CFO Grade report now tracks your{" "}
              <span className="font-medium">enterprise value</span> — see what
              your business is worth today and how much you built (or lost)
              since last quarter.
            </span>
            <span className="sm:hidden">
              Every report now tracks your{" "}
              <span className="font-medium">enterprise value</span>.
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/analyze?utm_source=whats_new_banner"
            onClick={dismiss}
            className="hidden sm:inline text-[12.5px] font-medium underline underline-offset-2 text-[#F7F5F0] hover:text-[#C26D5C] transition-colors whitespace-nowrap"
            data-testid="whats-new-banner-cta"
          >
            Try it now
          </Link>
          <button
            onClick={dismiss}
            className="text-[#F7F5F0]/70 hover:text-[#F7F5F0] transition-colors p-1 -mr-1"
            aria-label="Dismiss"
            data-testid="whats-new-banner-dismiss"
          >
            <X size={14} strokeWidth={1.75} />
          </button>
        </div>
      </div>
    </div>
  );
}
